/*
 * promocoesHandler.ts — busca as promoções da marca para o card "Promoções Ativas".
 *
 * Chamado SOB DEMANDA, quando o apostador toca no card — nunca no load do chat. Quem não clica não
 * paga o payload, e o caminho crítico de abertura não muda.
 *
 * ⚠️ REGRA NÚMERO UM DAQUI: nada nesta função pode lançar. O widget roda no navegador, dentro da
 * página do site de apostas, e um erro em runtime não degrada um pedaço da tela — derruba a árvore
 * React inteira e o chat SOME. Foi o que custou 1h20 de webchat fora do ar em 28/07. Por isso toda
 * falha (rede, status, JSON, formato inesperado) vira lista vazia com `erro: true`.
 *
 * A sanitização do corpo é do SERVIDOR — mesma decisão dos artigos da Central, onde o
 * `publicHelpCenter` injeta o body já passado pela allowlist. Aqui NÃO se sanitiza de novo: seria
 * uma segunda régua, e a biblioteca que o servidor usa (`sanitize-html`) é de Node — trazê-la para
 * o bundle reproduziria exatamente a falha de 28/07 (`mime-types` → `require('path')` → TypeError).
 */

import { reportClientError } from "./utils/reportClientError";
import { descreverErro } from "./utils/descreverErro";

export interface PromocaoDoWidget {
    id: string;
    title: string;
    /** HTML JÁ sanitizado pelo servidor. */
    bodyHtml: string;
    /**
     * Datas em ISO — só vêm quando o operador ligou "mostrar as datas ao apostador" no portal.
     * Ausência aqui significa NÃO MOSTRAR: quem decide é o portal, e o widget não tem como (nem
     * por que) adivinhar. Opcionais também por retrocompatibilidade: um servidor mais antigo que o
     * bundle simplesmente não manda o campo, e a tela segue igual ao que era antes.
     */
    startsAt?: string | null;
    expiresAt?: string | null;
}

export interface TextosDoWidget {
    cardTitle: string;
    cardSubtitle: string;
    emptyMessage: string;
}

export interface RespostaPromocoes {
    promocoes: PromocaoDoWidget[];
    textos: TextosDoWidget;
    /** `true` = não deu para buscar. A tela avisa em vez de fingir que a lista está vazia. */
    erro: boolean;
}

/*
 * Defaults de EMERGÊNCIA — só valem se o servidor não mandar textos (contrato driftou, deploy fora
 * de ordem). Quem escreve esses textos é a marca, no portal.
 *
 * O `emptyMessage` não afirma ausência de propósito: o widget não sabe o que existe, sabe o que
 * recebeu. Negar existência seria falar de um estado que ele não consegue verificar.
 */
const TEXTOS_PADRAO: TextosDoWidget = {
    cardTitle: "Promoções Ativas",
    cardSubtitle: "Novidades e ofertas",
    emptyMessage: "Em breve, novidades por aqui."
};

/** O que devolvemos quando não há nem o que tentar (URL não configurada no build). */
export const PROMOCOES_VAZIO: RespostaPromocoes = {
    promocoes: [],
    textos: TEXTOS_PADRAO,
    erro: false
};

/** Fica só com item que tem id E título — o resto não tem como ser desenhado nem aberto. */
const normalizar = (bruto: unknown): PromocaoDoWidget[] => {
    if (!Array.isArray(bruto)) return [];
    return bruto
        .filter((p): p is Record<string, unknown> => !!p && typeof p === "object")
        .filter((p) => typeof p.id === "string" && !!p.id && typeof p.title === "string" && !!p.title)
        .map((p) => ({
            id: String(p.id),
            title: String(p.title),
            bodyHtml: typeof p.bodyHtml === "string" ? p.bodyHtml : "",
            /*
             * As datas só existem quando o operador pediu para mostrá-las — o servidor omite as
             * outras. Espalhar condicionalmente preserva essa distinção: "não veio" continua
             * `undefined`, e não vira `null`, que a tela teria de aprender a distinguir.
             *
             * ⚠️ Este `spread` já foi esquecido uma vez (N3 de 2026-08-06): o objeto era montado
             * campo a campo, o servidor mandava as datas e elas morriam aqui, invisíveis para os
             * testes de componente, que mockam esta função inteira.
             */
            ...(typeof p.startsAt === "string" ? { startsAt: p.startsAt } : {}),
            ...(typeof p.expiresAt === "string" ? { expiresAt: p.expiresAt } : {})
        }));
};

const normalizarTextos = (bruto: unknown): TextosDoWidget => {
    const t = (bruto && typeof bruto === "object" ? bruto : {}) as Record<string, unknown>;
    const texto = (v: unknown, padrao: string) => (typeof v === "string" && v.trim() ? v.trim() : padrao);
    return {
        cardTitle: texto(t.cardTitle, TEXTOS_PADRAO.cardTitle),
        cardSubtitle: texto(t.cardSubtitle, TEXTOS_PADRAO.cardSubtitle),
        emptyMessage: texto(t.emptyMessage, TEXTOS_PADRAO.emptyMessage)
    };
};

export async function getPromocoes(brand: string): Promise<RespostaPromocoes> {
    const baseUrl = (process.env.REACT_APP_PROMOCOES_URL || "").trim();
    if (!baseUrl) return PROMOCOES_VAZIO;

    try {
        const separador = baseUrl.includes("?") ? "&" : "?";
        const resposta = await fetch(`${baseUrl}${separador}brand=${encodeURIComponent(brand)}`, {
            method: "GET",
            headers: { Accept: "application/json" }
        });
        if (!resposta.ok) {
            // Este caminho era o MAIS silencioso dos dois: saía por um `return` sem nem um console.
            reportClientError("promotions_load_failed", { reason: `HTTP ${resposta.status}` });
            return { ...PROMOCOES_VAZIO, erro: true };
        }

        const corpo = (await resposta.json()) as Record<string, unknown>;
        return {
            promocoes: normalizar(corpo?.promocoes),
            textos: normalizarTextos(corpo?.textos),
            erro: false
        };
    } catch (e) {
        // Rede, JSON malformado, qualquer coisa: a lista fica vazia e a tela avisa. O que NÃO pode
        // acontecer é a exceção subir — ver o comentário do topo.
        //
        // A tela avisa o APOSTADOR; o relato avisa a OPERAÇÃO. Sem ele o endpoint podia ficar fora
        // por semanas com todo mundo vendo "não foi possível carregar" e ninguém sabendo.
        reportClientError("promotions_load_failed", { reason: descreverErro(e) });
        return { ...PROMOCOES_VAZIO, erro: true };
    }
}
